//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { partition } from 'lodash';

import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import { IDefaultInstagramAccount } from '@instagram-account/redux/account/instagramAccountModel';
import { UniversalItemType } from '@models/UniversalItemType';
import { IConnectedBrandInstagramBusinessAccounts } from '@apis/instagram-settings/gatewayInstagramSettingsApiModel';
import { ITikTokSettingsState } from '@account-settings/redux/tiktok/tiktokSettingsModel';
import { ITikTokUser } from '@apis/tiktok-user/tiktokUserApiModel';

export interface ISplitMentionedItems {
  atMentionedItems: IContentLibraryItem[];
  nonAtMentionedItems: IContentLibraryItem[];
}

export const splitMentionedItemsCombiner = (
  materializedItems: IContentLibraryItem[],
  defaultInstagramAccount: IDefaultInstagramAccount | undefined,
  connectedInstagramAccounts:
    | IConnectedBrandInstagramBusinessAccounts[]
    | undefined,
  isMultiHandleFeatureEnabled: boolean,
  isTikTokMentionedRightsEnabled: boolean,
  tikTokAccountData: ITikTokSettingsState,
  tikTokUser: ITikTokUser | undefined,
): ISplitMentionedItems => {
  const tikTokUsername =
    tikTokUser?.username || tikTokAccountData?.connectedAccount?.displayName;
  if (!defaultInstagramAccount && !tikTokUsername) {
    return {
      atMentionedItems: [],
      nonAtMentionedItems: [],
    };
  }

  const socialMediaItems = materializedItems.filter((contentItem) => {
    return (
      contentItem.itemType === UniversalItemType.Instagram ||
      contentItem.itemType === UniversalItemType.Facebook ||
      (contentItem.itemType === UniversalItemType.TikTok &&
        isTikTokMentionedRightsEnabled)
    );
  });

  const [atMentionedItems, nonAtMentionedItems] = partition(
    socialMediaItems,
    (contentItem: IContentLibraryItem) => {
      const accountUsernamesSet = new Set(
        connectedInstagramAccounts?.map((account) =>
          account.username.toLowerCase(),
        ),
      );
      if (tikTokUsername) {
        accountUsernamesSet.add(tikTokUsername.toLowerCase());
      }

      return contentItem.socialDetails?.mentionedUsers.find((socialUser) => {
        const socialUserName = socialUser.username.toLowerCase();
        if (isMultiHandleFeatureEnabled) {
          return accountUsernamesSet.has(socialUserName);
        } else {
          return contentItem.itemType === UniversalItemType.TikTok
            ? socialUserName === tikTokUsername?.toLowerCase()
            : socialUserName ===
                defaultInstagramAccount?.username?.toLowerCase();
        }
      });
    },
  );

  return {
    atMentionedItems,
    nonAtMentionedItems,
  };
};
